import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, KeyRound, Smartphone, Monitor } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";
import { Card, useToast, Avatar } from "@/components/ui";
import { Field, TextInput } from "@/components/fields";
import { fmtDateTime } from "@/lib/format";

export function Profile() {
  const { user, refresh } = useAuth();
  const toast = useToast();

  return (
    <>
      <PageHeader title="Profile & security" />
      <div className="grid gap-4 lg:grid-cols-2">
        <ProfileCard />
        <PasswordCard onDone={() => toast.push("success", "Password changed")} />
        <TwoFactorCard onChange={refresh} />
        <SessionsCard />
      </div>
      <p className="mt-4 text-xs text-ink-faint">
        Signed in as <b>@{user?.username}</b> · Roles: {user?.roles.map((r) => r.name).join(", ")}
      </p>
    </>
  );
}

function ProfileCard() {
  const { user, setUser } = useAuth();
  const toast = useToast();
  const [f, setF] = useState({ fullName: user?.fullName ?? "", jobTitle: user?.jobTitle ?? "", phone: "" });
  const save = async () => {
    try {
      const { user: u } = await api.put<{ user: any }>("/auth/preferences", f);
      setUser(u); toast.push("success", "Profile saved");
    } catch (e: any) { toast.push("error", e?.message || "Failed"); }
  };
  return (
    <Card>
      <div className="mb-3 flex items-center gap-3">
        <Avatar name={user?.fullName || "?"} color={user?.avatarColor} size={40} />
        <h3 className="text-sm font-semibold">Your details</h3>
      </div>
      <div className="space-y-3">
        <Field label="Full name"><TextInput value={f.fullName} onChange={(e) => setF({ ...f, fullName: e.target.value })} /></Field>
        <Field label="Job title"><TextInput value={f.jobTitle} onChange={(e) => setF({ ...f, jobTitle: e.target.value })} /></Field>
        <Field label="Phone"><TextInput value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
        <button className="btn-primary" onClick={save}>Save</button>
      </div>
    </Card>
  );
}

function PasswordCard({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({ currentPassword: "", newPassword: "", confirm: "" });
  const [err, setErr] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (f.newPassword !== f.confirm) { setErr("Passwords do not match"); return; }
    try {
      await api.post("/auth/change-password", { currentPassword: f.currentPassword, newPassword: f.newPassword });
      setF({ currentPassword: "", newPassword: "", confirm: "" }); setErr(""); onDone();
    } catch (e) { setErr(e instanceof ApiError ? e.message : "Failed"); }
  };
  return (
    <Card>
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><KeyRound size={15} /> Change password</h3>
      <form onSubmit={submit} className="space-y-3">
        {err && <p className="rounded-lg bg-st-delayed/10 px-3 py-2 text-sm text-st-delayed">{err}</p>}
        <Field label="Current password"><TextInput type="password" value={f.currentPassword} onChange={(e) => setF({ ...f, currentPassword: e.target.value })} required /></Field>
        <Field label="New password (min 10)"><TextInput type="password" minLength={10} value={f.newPassword} onChange={(e) => setF({ ...f, newPassword: e.target.value })} required /></Field>
        <Field label="Confirm"><TextInput type="password" minLength={10} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} required /></Field>
        <button className="btn-primary">Update password</button>
      </form>
    </Card>
  );
}

function TwoFactorCard({ onChange }: { onChange: () => void }) {
  const { user } = useAuth();
  const toast = useToast();
  const [setup, setSetup] = useState<{ secret: string; otpauth: string } | null>(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");

  const begin = async () => setSetup(await api.post("/auth/2fa/setup"));
  const enable = async () => {
    try { await api.post("/auth/2fa/enable", { code }); setSetup(null); onChange(); toast.push("success", "Two-factor enabled"); }
    catch (e: any) { toast.push("error", e?.message || "Invalid code"); }
  };
  const disable = async () => {
    try { await api.post("/auth/2fa/disable", { password }); onChange(); toast.push("success", "Two-factor disabled"); }
    catch (e: any) { toast.push("error", e?.message || "Failed"); }
  };

  return (
    <Card>
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><ShieldCheck size={15} /> Two-factor authentication</h3>
      {user?.totpEnabled ? (
        <div className="space-y-3">
          <p className="chip bg-st-ontrack/15 text-st-ontrack"><Smartphone size={13} /> Enabled</p>
          <Field label="Confirm with password to disable"><TextInput type="password" value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
          <button className="btn-danger" onClick={disable}>Disable 2FA</button>
        </div>
      ) : setup ? (
        <div className="space-y-3">
          <p className="text-sm text-ink-soft">Scan this URL in your authenticator app, or enter the key manually:</p>
          <code className="block break-all rounded-lg bg-surface-2 p-2 text-xs">{setup.otpauth}</code>
          <p className="text-xs text-ink-faint">Manual key: <b>{setup.secret}</b></p>
          <Field label="Enter the 6-digit code"><TextInput inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} /></Field>
          <button className="btn-primary" onClick={enable}>Verify &amp; enable</button>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-ink-soft">Add a second step at sign-in using an authenticator app (Google Authenticator, 1Password, Authy…).</p>
          <button className="btn-outline" onClick={begin}>Set up 2FA</button>
        </div>
      )}
    </Card>
  );
}

function SessionsCard() {
  const { data, refetch } = useQuery({ queryKey: ["my-sessions"], queryFn: () => api.get<{ sessions: any[] }>("/auth/sessions") });
  return (
    <Card>
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><Monitor size={15} /> Active sessions</h3>
      <ul className="space-y-2 text-sm">
        {(data?.sessions ?? []).map((s) => (
          <li key={s.id} className="flex items-center justify-between rounded-lg border border-line px-3 py-2">
            <span className="min-w-0">
              <span className="block truncate text-xs">{s.user_agent || "Unknown device"}</span>
              <span className="text-[11px] text-ink-faint">{s.ip} · last seen {fmtDateTime(s.last_seen_at)}</span>
            </span>
            {s.current ? <span className="chip bg-brand-50 text-brand-700">This device</span> :
              <button className="btn-ghost text-xs text-st-delayed" onClick={async () => { await api.del(`/auth/sessions/${s.id}`); refetch(); }}>Revoke</button>}
          </li>
        ))}
      </ul>
    </Card>
  );
}
